/**
 * PerfusedVolumeCard — summary card for a perfused volume.
 *
 * Shows name, volume, hepatic reserve with color coding, and edit/delete actions.
 */

import { Pencil, Trash2 } from 'lucide-react';

import type { IPerfusedVolume } from '../domain/perfused-volume';
import { getHepaticReserveStatus } from '../domain/perfused-volume';
import { SEGMENT_COLORS, ROMAN_NUMERALS, rgbToCss } from '../domain/segment-colors';

import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface PerfusedVolumeCardProps {
    pv: IPerfusedVolume;
    isSelected: boolean;
    onSelect: (id: string | null) => void;
    onEdit?: (id: string) => void;
    onRemove?: (id: string) => void;
    headerExtra?: React.ReactNode;
    children?: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export function PerfusedVolumeCard({
    pv,
    isSelected,
    onSelect,
    onEdit,
    onRemove,
    headerExtra,
    children,
}: PerfusedVolumeCardProps) {
    const reserveStatus = getHepaticReserveStatus(pv.hepaticReservePercent);

    return (
        <div
            className={cn(
                'group cursor-pointer rounded-md border px-3 py-2 transition-colors',
                isSelected
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
            )}
            onClick={() => onSelect(isSelected ? null : pv.id)}
        >
            {/* Header: name + actions */}
            <div className="flex justify-between gap-2">
                <span className="flex-1 truncate text-sm font-medium">{pv.name}</span>
                {headerExtra}
                {(onEdit || onRemove) && (
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {onEdit && (
                            <button
                                className="rounded p-0.5 hover:bg-muted"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(pv.id);
                                }}
                                title="Edit"
                            >
                                <Pencil className="h-3 w-3" />
                            </button>
                        )}
                        {onRemove && (
                            <button
                                className="rounded p-0.5 hover:bg-muted"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemove(pv.id);
                                }}
                                title="Remove"
                            >
                                <Trash2 className="h-3 w-3 hover:text-destructive transition-colors" />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Segment pills */}
            <div className="mt-1.5 flex flex-wrap gap-1">
                {pv.segmentIndices.map((lv) => {
                    const color = SEGMENT_COLORS[lv] ?? [0.6, 0.6, 0.6];
                    return (
                        <span
                            key={lv}
                            className="inline-flex items-center gap-0.5 rounded-sm bg-muted/60 px-1.5 py-0.5 text-[10px] font-mono font-medium"
                        >
                            <span
                                className="inline-block h-2 w-2 rounded-sm"
                                style={{ backgroundColor: rgbToCss(color) }}
                            />
                            {ROMAN_NUMERALS[lv] ?? lv}
                        </span>
                    );
                })}
            </div>

            {/* Stats */}
            <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
                <span className="tabular-nums font-mono">{pv.volumeMl.toFixed(1)} mL</span>
                {pv.tumorVolumeMl > 0 && (
                    <span className="tabular-nums font-mono">
                        {pv.tumorIndices.length} tumor{pv.tumorIndices.length !== 1 ? 's' : ''}
                    </span>
                )}
                <span
                    className={cn(
                        'ml-auto tabular-nums font-mono font-medium',
                        reserveStatus === 'critical' && 'text-red-500',
                        reserveStatus === 'warning' && 'text-orange-500'
                    )}
                >
                    Reserve {pv.hepaticReservePercent.toFixed(1)}%
                </span>
            </div>

            {children}
        </div>
    );
}
